import React, { useState, useEffect } from 'react';
import { getActiveMatter, useActiveMatter } from '../lib/activeMatter';
import { runAiJob } from '../lib/aiJob';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Shield, Brain, Users, Scale, TrendingUp, Target, Globe,
  ChevronDown, Search, Filter, ArrowRight, BarChart2,
  AlertTriangle, CheckCircle, Info, Zap, Award, Gavel
} from 'lucide-react';
import { StrategyPanel } from '../components/judicial/StrategyPanel';
import { JudgeProfileDialog } from '../components/judicial/JudgeProfileDialog';
import { CompareJudges } from '../components/judicial/CompareJudges';
import { RulingsFeed } from '../components/judicial/RulingsFeed';
import { saveBlob } from '../components/judicial/downloadPdf';
import { Download } from 'lucide-react';
import { CerebroFidem } from '../components/fidem/CerebroFidem';

const COUNTRIES = [
  { code: 'chile', name: 'Chile', flag: '🇨🇱' },
  { code: 'ecuador', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'argentina', name: 'Argentina', flag: '🇦🇷' },
  { code: 'mexico', name: 'México', flag: '🇲🇽' },
  { code: 'colombia', name: 'Colombia', flag: '🇨🇴' },
  { code: 'peru', name: 'Perú', flag: '🇵🇪' }
];

const CASE_TYPES = [
  { value: 'laboral', label: 'Laboral' },
  { value: 'civil', label: 'Civil' },
  { value: 'penal', label: 'Penal' },
  { value: 'constitucional', label: 'Constitucional' },
  { value: 'tributario', label: 'Tributario' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'familia', label: 'Familia' },
  { value: 'comercial', label: 'Comercial' }
];

const COURT_LEVELS = [
  { value: 'corte_suprema', label: 'Corte Suprema' },
  { value: 'tribunal_constitucional', label: 'Tribunal Constitucional' },
  { value: 'corte_nacional', label: 'Corte Nacional de Justicia' },
  { value: 'corte_constitucional', label: 'Corte Constitucional' },
  { value: 'corte_apelaciones', label: 'Corte de Apelaciones' }
];

const JudicialIntelligencePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [judges, setJudges] = useState([]);
  const [prediction, setPrediction] = useState(null);
  const [cerebroStatus, setCerebroStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const activeCase = getActiveMatter();
  const [liveCase] = useActiveMatter();
  const [caseSummary, setCaseSummary] = useState(() => (activeCase ? `Causa activa: ${activeCase.title}` : ''));
  const [profileJudgeId, setProfileJudgeId] = useState(null);
  
  // Filters
  const [selectedCountry, setSelectedCountry] = useState(activeCase?.country || 'chile');
  const [selectedCaseType, setSelectedCaseType] = useState(activeCase?.matter_type && activeCase.matter_type !== 'disciplinario' ? activeCase.matter_type : 'laboral');
  const [selectedCourtLevel, setSelectedCourtLevel] = useState('corte_suprema');
  const firstCase = React.useRef(activeCase?.id);
  useEffect(() => {
    if (!liveCase || liveCase.id === firstCase.current) return;
    firstCase.current = liveCase.id;
    setSelectedCountry(liveCase.country || 'chile');
    if (liveCase.matter_type && liveCase.matter_type !== 'disciplinario') setSelectedCaseType(liveCase.matter_type);
    setCaseSummary(`Causa activa: ${liveCase.title}`);
  }, [liveCase]);

  useEffect(() => {
    loadData();
  }, [selectedCountry]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsRes = await api.get('/judicial-intelligence/stats');
      setStats(statsRes.data);
      
      // Load judges for selected country
      const judgesRes = await api.get(`/judicial-intelligence/judges?country=${selectedCountry}&limit=100`);
      setJudges(judgesRes.data.judges || []);
      
      // Load Cerebro status
      const cerebroRes = await api.get('/judicial-intelligence/cerebro/status');
      setCerebroStatus(cerebroRes.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos de inteligencia judicial');
    } finally {
      setLoading(false);
    }
  };

  const getPrediction = async () => {
    try {
      setPredicting(true);
      const res = await api.get(
        `/judicial-intelligence/predict/${selectedCountry}/${selectedCaseType}?court_level=${selectedCourtLevel}`
      );
      setPrediction(res.data);
      toast.success('Predicción generada. Cerebro prepara la estrategia...');
    } catch (error) {
      console.error('Error getting prediction:', error);
      toast.error('Error al generar predicción');
      return;
    } finally {
      setPredicting(false);
    }
    setStrategy(null);
    setStrategyLoading(true);
    try {
      const result = await runAiJob(api, '/judicial-intelligence/strategy', {
        country: selectedCountry,
        case_type: selectedCaseType,
        court_level: selectedCourtLevel,
        case_summary: caseSummary || null
      });
      setStrategy(result.strategy);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al generar la estrategia');
    } finally {
      setStrategyLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.post('/judicial-intelligence/strategy/pdf', {
        strategy,
        title: caseSummary ? caseSummary.slice(0, 90) : `Caso ${selectedCaseType}`,
        country: COUNTRIES.find(c => c.code === selectedCountry)?.name,
        case_type: selectedCaseType
      }, { responseType: 'blob' });
      saveBlob(res.data, 'estrategia-victoria-notaryos.pdf');
    } catch {
      toast.error('No se pudo generar el PDF');
    }
  };

  const getTendencyColor = (tendency) => {
    const colors = {
      garantista: 'text-green-400 bg-green-400/10',
      pro_trabajador: 'text-blue-400 bg-blue-400/10',
      pro_ciudadano: 'text-emerald-400 bg-emerald-400/10',
      formalista: 'text-amber-400 bg-amber-400/10',
      pro_estado: 'text-red-400 bg-red-400/10',
      neutral: 'text-gray-400 bg-gray-400/10',
      finalista: 'text-purple-400 bg-purple-400/10',
      pro_empresa: 'text-orange-400 bg-orange-400/10'
    };
    return colors[tendency] || 'text-gray-400 bg-gray-400/10';
  };

  const getWinRateColor = (rate) => {
    if (rate >= 0.7) return 'text-green-400';
    if (rate >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const selectedCountryData = COUNTRIES.find(c => c.code === selectedCountry);
  const countryStats = stats?.by_country?.[selectedCountry];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white">
                <Shield className="w-8 h-8 text-blue-500" />
              </button>
              <span className="text-xl font-bold">Inteligencia Judicial</span>
              <Brain className="w-6 h-6 text-purple-400 animate-pulse" />
            </div>
            
            {/* Country Selector */}
            <div className="flex items-center gap-4">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white"
                data-testid="country-select"
              >
                {COUNTRIES.map(country => (
                  <option key={country.code} value={country.code}>{`${country.flag} ${country.name}`}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap gap-5 border-b border-slate-700 pb-4"><button data-testid="cerebro-vocabulary-link" onClick={() => navigate('/vocabulario-juridico')} className="text-sm font-semibold text-emerald-300 hover:text-emerald-100">Vocabulario jurídico</button><button data-testid="cerebro-vocabulary-quality" onClick={() => navigate('/vocabulario-juridico/calidad')} className="text-sm text-sky-300 hover:text-sky-100">Clasificación y calidad del conocimiento</button></div>
        <CerebroFidem />
        {/* Cerebro Status Banner */}
        {cerebroStatus && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-white font-semibold">Cerebro IA - Sistema de Aprendizaje</h3>
                  <p className="text-slate-400 text-sm">
                    {cerebroStatus.learning_status}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{cerebroStatus.total_data_points}</p>
                <p className="text-xs text-slate-400">Datos alimentados</p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_judges_all_countries}</p>
                  <p className="text-sm text-slate-400">Jueces/Ministros</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.countries_covered}</p>
                  <p className="text-sm text-slate-400">Países</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Scale className="w-8 h-8 text-amber-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{countryStats?.total_judges || 0}</p>
                  <p className="text-sm text-slate-400">En {selectedCountryData?.name}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Gavel className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {Object.keys(countryStats?.by_court_level || {}).length}
                  </p>
                  <p className="text-sm text-slate-400">Cortes analizadas</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Strategic Prediction */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-400" />
            Predicción Estratégica
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">País</label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Tipo de Caso</label>
              <select
                value={selectedCaseType}
                onChange={(e) => setSelectedCaseType(e.target.value)}
                data-testid="case-type-select"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              >
                {CASE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nivel de Corte</label>
              <select
                value={selectedCourtLevel}
                onChange={(e) => setSelectedCourtLevel(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
              >
                {COURT_LEVELS.map(l => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={getPrediction}
                disabled={predicting}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="predict-btn"
              >
                {predicting ? (
                  <>Analizando...</>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Predecir Victoria
                  </>
                )}
              </button>
            </div>
          </div>

          <textarea
            data-testid="case-summary-input"
            value={caseSummary}
            onChange={(e) => setCaseSummary(e.target.value)}
            placeholder="Opcional: describe tu caso (hechos, qué pides, pruebas) para una estrategia a medida..."
            rows={2}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
          />

          <StrategyPanel
            strategy={strategy}
            loading={strategyLoading}
            actions={
              <button data-testid="strategy-pdf-btn" onClick={downloadPdf} className="text-xs bg-amber-500 text-slate-950 font-semibold rounded-full px-3 py-1 flex items-center gap-1">
                <Download className="w-3 h-3" />PDF para cliente
              </button>
            }
          />

          {/* Prediction Results */}
          {prediction && (
            <div className="mt-6 space-y-4 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Average Win Rate */}
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-600">
                  <p className="text-sm text-slate-400 mb-1">Tasa de Victoria Promedio</p>
                  <p className={`text-4xl font-bold ${getWinRateColor(prediction.average_win_rate)}`}>
                    {Math.round(prediction.average_win_rate * 100)}%
                  </p>
                </div>
                
                {/* Best Scenario */}
                {prediction.best_scenario && (
                  <div className="bg-green-900/20 rounded-xl p-4 border border-green-500/30">
                    <p className="text-sm text-green-400 mb-1 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Mejor Escenario
                    </p>
                    <p className="text-white font-semibold">{prediction.best_scenario.judge_name}</p>
                    <p className="text-green-400 text-2xl font-bold">
                      {Math.round(prediction.best_scenario.predicted_win_rate * 100)}%
                    </p>
                    <span className={`text-xs px-2 py-1 rounded ${getTendencyColor(prediction.best_scenario.key_tendency)}`}>
                      {prediction.best_scenario.key_tendency.replace('_', ' ')}
                    </span>
                  </div>
                )}
                
                {/* Worst Scenario */}
                {prediction.worst_scenario && prediction.predictions.length > 1 && (
                  <div className="bg-red-900/20 rounded-xl p-4 border border-red-500/30">
                    <p className="text-sm text-red-400 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> Peor Escenario
                    </p>
                    <p className="text-white font-semibold">{prediction.worst_scenario.judge_name}</p>
                    <p className="text-red-400 text-2xl font-bold">
                      {Math.round(prediction.worst_scenario.predicted_win_rate * 100)}%
                    </p>
                    <span className={`text-xs px-2 py-1 rounded ${getTendencyColor(prediction.worst_scenario.key_tendency)}`}>
                      {prediction.worst_scenario.key_tendency.replace('_', ' ')}
                    </span>
                  </div>
                )}
              </div>

              {/* All Judges Predictions */}
              {prediction.predictions && prediction.predictions.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Predicciones por Juez</h3>
                  <div className="space-y-2">
                    {prediction.predictions.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                            <Scale className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{p.judge_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${getTendencyColor(p.key_tendency)}`}>
                              {p.key_tendency.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getWinRateColor(p.predicted_win_rate)}`}>
                            {Math.round(p.predicted_win_rate * 100)}%
                          </p>
                          <p className="text-xs text-slate-400">probabilidad</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <CompareJudges judges={judges} caseTypes={CASE_TYPES} />

        <RulingsFeed country={selectedCountry} onNewRulings={loadData} />

        {/* Judges List */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-400" />
            Ministros y Jueces - {selectedCountryData?.flag} {selectedCountryData?.name}
          </h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-slate-400 mt-4">Cargando jueces...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {judges.map((judge) => (
                <button
                  type="button"
                  key={judge.id}
                  data-testid={`judge-card-${judge.id}`}
                  onClick={() => setProfileJudgeId(judge.id)}
                  className="text-left bg-slate-900/50 rounded-xl p-4 border border-slate-700 hover:border-amber-500/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-semibold">{judge.name}</h3>
                      <p className="text-sm text-slate-400">{judge.position}</p>
                    </div>
                    {judge.sala && (
                      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                        {judge.sala}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs text-slate-500 mb-3">{judge.court_name}</p>
                  
                  {/* Specializations */}
                  {judge.specialization && judge.specialization.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {judge.specialization.slice(0, 3).map((spec, idx) => (
                        <span key={idx} className="text-xs bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded">
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Tendencies */}
                  {judge.tendency_score && Object.keys(judge.tendency_score).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(judge.tendency_score)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 2)
                        .map(([tendency, score]) => (
                          <div key={tendency} className="flex items-center justify-between">
                            <span className={`text-xs px-2 py-0.5 rounded ${getTendencyColor(tendency)}`}>
                              {tendency.replace('_', ' ')}
                            </span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${score * 100}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-400">{Math.round(score * 100)}%</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  <p className="text-xs text-amber-400 mt-3">Ver ficha completa →</p>
                </button>
              ))}
            </div>
          )}
        </div>
        <JudgeProfileDialog judgeId={profileJudgeId} onClose={() => setProfileJudgeId(null)} />
      </main>
    </div>
  );
};

export default JudicialIntelligencePage;
